import {
  isSessionOnlyAuth,
  getLastSessionActivity,
  recordSessionActivity,
  hasSessionCookie
} from '../../firebase/authUtils';

const handleSessionPing = (data, channel) => {
  const currentTabSession = typeof window !== 'undefined' ? window.sessionStorage.getItem('tas_session_id') : null;
  if (currentTabSession && currentTabSession === data.sessionId) {
    try {
      channel.postMessage({ type: 'SESSION_PONG', sessionId: data.sessionId });
    } catch { /* ignore */ }
  }
};

const handleSessionPong = (data, probeState) => {
  const activeLocalId = typeof window !== 'undefined' ? window.localStorage.getItem('tas_session_id') : null;
  if (activeLocalId && activeLocalId === data.sessionId) {
    if (probeState.timerId) {
      clearTimeout(probeState.timerId);
      probeState.timerId = null;
    }
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('tas_session_id', data.sessionId);
      window.sessionStorage.setItem('tas_session_only', 'true');
    }
  }
};

export const handleBroadcastMessage = ({ data, channel, probeState, onSignOut, onExtend }) => {
  if (!data || typeof data !== 'object') return;

  switch (data.type) {
    case 'SESSION_PING':
      if (data.sessionId) handleSessionPing(data, channel);
      break;
    case 'SESSION_PONG':
      if (data.sessionId) handleSessionPong(data, probeState);
      break;
    case 'SESSION_ACTIVITY':
    case 'SESSION_EXTEND':
      onExtend();
      break;
    case 'SESSION_SIGN_OUT':
      onSignOut(data.reason || 'timeout');
      break;
    default:
      break;
  }
};

export const setupCrossTabProbe = ({ onSignOut }) => {
  const localSessionId = typeof window !== 'undefined' ? window.localStorage.getItem('tas_session_id') : null;
  const currentSessionId = typeof window !== 'undefined' ? window.sessionStorage.getItem('tas_session_id') : null;

  if (localSessionId && currentSessionId !== localSessionId) {
    if (hasSessionCookie()) {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('tas_session_id', localSessionId);
        window.sessionStorage.setItem('tas_session_only', 'true');
      }
      return;
    }

    onSignOut('browser_closed');
  }
};

export const createVisibilityHandler = ({ timeoutMs, handleSignOut, checkTimeout, lastRecordedRef }) => () => {
  if (document.visibilityState === 'visible') {
    if (!isSessionOnlyAuth()) return;
    if (!hasSessionCookie()) {
      handleSignOut('browser_closed');
      return;
    }
    const lastActivity = getLastSessionActivity();
    const currentNow = Date.now();
    if (lastActivity && (currentNow - lastActivity < timeoutMs)) {
      recordSessionActivity();
      lastRecordedRef.current = currentNow;
    }
    checkTimeout();
  }
};

export const createStorageHandler = ({ checkTimeout, lastRecordedRef, setShowWarning }) => (e) => {
  if (e.key === 'tas_keep_logged_in' || e.key === 'pendingKeepLoggedIn' || e.key === 'tas_session_only') {
    if (!isSessionOnlyAuth()) setShowWarning(false);
    else checkTimeout();
  } else if (e.key === 'tas_last_activity' && isSessionOnlyAuth()) {
    lastRecordedRef.current = Date.now();
    checkTimeout();
  }
};

export const bindWindowListeners = ({ updateActivity, handleVisibilityOrFocus, handleStorageChange }) => {
  const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'mousemove'];
  events.forEach((evt) => window.addEventListener(evt, updateActivity, { passive: true }));
  window.addEventListener('visibilitychange', handleVisibilityOrFocus);
  window.addEventListener('focus', handleVisibilityOrFocus);
  window.addEventListener('storage', handleStorageChange);

  return () => {
    events.forEach((evt) => window.removeEventListener(evt, updateActivity));
    window.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    window.removeEventListener('focus', handleVisibilityOrFocus);
    window.removeEventListener('storage', handleStorageChange);
  };
};
